
import { ProductListingPage } from "@decocms/apps-commerce/types"
import { isEagerRequest } from "@decocms/blocks/cms"
import { canonicalFromBreadcrumblist } from "@decocms/apps-commerce/utils/canonical"

import { buildSeoHeadPayload, Props as SeoProps } from "~/sections/SEO/CustomSEO"
import { PLPcsv } from "~/loaders/PLPCsv"
import breadcrumbOrCurrent from "~/sdk/breadcrumbOrCurrent"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import streamCSV from "~/sdk/streamCSV"

export const seo = true;

import { urlify } from "~/sdk/urlify"
import URLtoBreadcrumb from "~/sdk/URLtoBreadcrumb"
import { capitalize } from "~/sdk/utils/capitalize"
import { zip } from "~/sdk/utils/zip"
import { SITE } from "~/constants"

type CsvLine = {
    url?: string
    title?: string
    description?: string
    noindex?: string
    nofollow?: string
}

type CsvIndex = {
    exactPatterns: Array<{ pattern: string; line: CsvLine }>
    regexPatterns: Array<{ pattern: string; regex: RegExp; line: CsvLine }>
    rows: number
    expiresAt: number
}

const CSV_INDEX_TTL_MS = 5 * 60 * 1000
const csvIndexCache = new Map<string, CsvIndex>()

const getCsvIndex = async (csvUrl: string) => {
    const cached = csvIndexCache.get(csvUrl)
    if (cached && Date.now() <= cached.expiresAt) return { index: cached, cacheHit: true }

    const exactPatterns: Array<{ pattern: string; line: CsvLine }> = []
    const regexPatterns: Array<{ pattern: string; regex: RegExp; line: CsvLine }> = []
    let rows = 0

    for await (const line of streamCSV(csvUrl)) {
        rows += 1
        const { url: urlRegex } = line as CsvLine

        if (!urlRegex) throw new Error('url not found')

        if (/[.*+?^${}()|[\]\\]/.test(urlRegex)) {
            regexPatterns.push({ pattern: urlRegex, regex: new RegExp(urlRegex), line })
        } else {
            exactPatterns.push({ pattern: urlRegex, line })
        }
    }

    const index: CsvIndex = {
        exactPatterns,
        regexPatterns,
        rows,
        expiresAt: Date.now() + CSV_INDEX_TTL_MS,
    }

    csvIndexCache.set(csvUrl, index)

    return { index, cacheHit: false }
}

interface Search {
    /**
     * @title Template do TÍTULO da busca
     * @description %s - Miess
     */
    titleTemplate: string
    /**
     * @title Template da DESCRIÇÃO da busca
     * @description %s - Miess
     */
    descriptionTemplate: string
}

interface Department {
    /**
     * @title Template do TÍTULO do departamento
     * @description %s - Miess
     */
    titleTemplate: string
    /**
     * @title Template da TÍTULO caso a página tenha algum filtro selecionado (sex-shop/3r-import)
     * @description %s - Miess
     */
    someFilterTitleTemplate: string
    /**
     * @title Template da DESCRIÇÃO caso a página tenha algum filtro selecionado (sex-shop/3r-import)
     * @description %s - Miess
     */
    someFilterDescriptionTemplate: string
}

export interface Props extends
    Partial<
        Omit<
            SeoProps,
            | 'jsonLDs'
            | 'noIndexing'
            | 'title'
            | 'description'
            | 'titleTemplate'
            | 'descriptionTemplate'
            | 'type'
            | 'themeColor'
        >
    > {
    jsonLD: ProductListingPage | null
    /**
     * @title Busca
     */
    search: Search
    /**
     * @title Departamento
     */
    department: Department
    /**
     * @title Incluir os dados de produtos nos dados estruturados
     * @description Ativar essa opção pode impactar no carregamento da página, em beneficio do SEO
     * @default false
     */
    includesProductsInStructuredData?: boolean
    /**
     * @title CSV para configurar as páginas do departamento/busca
     */
    csv?: PLPcsv

    /**
     * @title Desabilitar indexação de buscas nos departamento (noindex, nofollow)
     */
    noIndexingDepartmentSearch?: boolean
}

export const loader = async (props: Props & { __pageUrl?: string }, request?: Request) => {
    const startAt = Date.now()
    const url = new URL(props.__pageUrl ?? "/", SITE.URL)
    const storeURL = url.origin
    const pageUrl = props.__pageUrl ?? url.href

    const pageTypes = props?.jsonLD?.pageInfo?.pageTypes ?? []
    const pageType = pageTypes.at(-1)!

    const mapParam = url.searchParams.get('map')
    const isSearchPage = pageType === 'Search' || url.pathname === '/s'
    const isDepartmentSearch = mapParam?.includes('ft') && mapParam?.includes('c')
    const isBrandPage = pageType === 'Brand'

    let title: string | undefined = undefined
    let description: string | undefined = undefined

    let noindex = false
    let nofollow = false

    if (props.noIndexingDepartmentSearch ?? true) {
        if (pageTypes.length > 1 && mapParam) {
            const maps = mapParam.split(',')
            const isSearch = maps.some((item) => item === 'ft')
            const isDepartment = maps.some((item) => item === 'c')

            if (isSearch && isDepartment) {
                noindex = true
                nofollow = false
            }
        }
    }

    if (props.csv) {
        const csvStartAt = Date.now()
        const { index, cacheHit } = await getCsvIndex(props.csv)
        let matched = false
        let matchedType: 'exact' | 'regex' | undefined
        let matchedPattern: string | undefined

        for (const { pattern, line } of index.exactPatterns) {
            if (url.href.includes(pattern)) {
                const { title: csvTitle, description: csvDescription, noindex: csvNoindex, nofollow: csvNofollow } =
                    line

                title = csvTitle
                description = csvDescription

                if (csvNoindex === '1') noindex = true
                if (csvNofollow === '1') nofollow = true

                matched = true
                matchedType = 'exact'
                matchedPattern = pattern
                break
            }
        }

        if (!matched) {
            for (const { pattern, regex, line } of index.regexPatterns) {
                if (regex.test(url.href)) {
                    const { title: csvTitle, description: csvDescription, noindex: csvNoindex, nofollow: csvNofollow } =
                        line

                    title = csvTitle
                    description = csvDescription

                    if (csvNoindex === '1') noindex = true
                    if (csvNofollow === '1') nofollow = true

                    matched = true
                    matchedType = 'regex'
                    matchedPattern = pattern
                    break
                }
            }
        }

        console.info('[SEO][PLP][CSV]', {
            path: url.pathname,
            rows: index.rows,
            cacheHit,
            matched,
            matchedType,
            matchedPattern,
            durationMs: Date.now() - csvStartAt,
        })
    }

    const endAt = Date.now()
    console.info('[SEO][PLP]', { path: url.pathname, durationMs: endAt - startAt })

    const enriched = {
        ...props,
        url: pageUrl,
        title,
        description,
        noindex,
        nofollow,
        storeURL,
        isSearchPage,
        isDepartmentSearch,
        isBrandPage,
    }

    // Head payload (title/meta/robots/canonical/OG/Twitter) computed here so
    // the route `head()` emits it once (see ~/components/seo/seoHead.ts).
    const head = buildPlpSeoHead(enriched)
    // JSON-LD is crawler-only. Strip it from the human payload (it stringifies
    // the whole ProductListingPage); bots / ?__deco_ssr=1 keep it.
    const seoForBot = isEagerRequest({
        userAgent: request?.headers.get("user-agent") ?? undefined,
        url: request?.url,
        request,
    })
    return { ...enriched, ...head, jsonLDs: seoForBot ? head.jsonLDs : [] }
}

function buildPlpSeoHead({
    jsonLD,
    url,
    isSearchPage,
    includesProductsInStructuredData = false,
    storeURL,
    isBrandPage,
    isDepartmentSearch,
    ...props
}: {
    jsonLD: ProductListingPage | null
    url: string
    isSearchPage: boolean
    includesProductsInStructuredData?: boolean
    storeURL: string
    isBrandPage: boolean
    isDepartmentSearch?: boolean
    title?: string
    description?: string
    noindex: boolean
    nofollow: boolean
} & Omit<Props, 'jsonLD'>) {
    const pageUrl = new URL(url ?? "/", SITE.URL)

    const map = decodeURIComponent(pageUrl.searchParams.get('map') || '').split(',')
    const paths = pageUrl.pathname.split('/').filter(Boolean)

    const urlMap = zip(map, paths)

    const breadcrumbItems = jsonLD?.breadcrumb.itemListElement ?? []

    const categories = breadcrumbOrCurrent(
        breadcrumbItems,
        urlMap.filter(([map]) => map === 'c').map(([, path]) => capitalize(path)),
    )
    const otherFilters = breadcrumbOrCurrent(
        breadcrumbItems,
        urlMap
            .filter(([map]) => map && map !== 'c')
            .map(([map, path]) => {
                if (map === 'b') return path.toUpperCase()
                return path
            }),
    ).map((i) => {
        const priceMatch = [...i.replaceAll('-', ' ').matchAll(/De (\d*\.?\d+) A (\d*\.?\d+)/gi)]

        if (priceMatch.length) {
            return `de ${formatPrice(Number(priceMatch[0][1]))?.replace(/\s/, '')} até ${
                formatPrice(
                    Number(priceMatch[0][2]),
                )?.replace(/\s/, '')
            }`
        }

        return i
    })

    const haveOneOrMoreFiltersAndCategories = categories.length >= 1 && otherFilters.length >= 1

    const hasOnlyCategory = categories.length > 1 && otherFilters.length === 0
    const hasNoFilterOrCategory = categories.length === 0 && otherFilters.length === 0

    const description = props.description || jsonLD?.seo?.description
    const canonical = props.canonical
        ? props.canonical
        : jsonLD?.seo?.canonical
        ? jsonLD.seo.canonical
        : jsonLD?.breadcrumb
        ? canonicalFromBreadcrumblist(jsonLD?.breadcrumb)
        : undefined

    if (!canonical) return { jsonLDs: [] as string[] }

    const canonicalUrl = new URL(canonical)
    const noIndexing = !jsonLD?.products.length
    const query = capitalize(decodeURIComponent(canonicalUrl.pathname).replace(/^\//, ''))

    const getCanonicalPath = (canonicalUrl: URL, isSearchPage: boolean) => {
        const params = new URLSearchParams(canonicalUrl.search)
        return isSearchPage
            ? `/s?q=${urlify(params.get('q')?.toLocaleLowerCase() ?? '')}`
            : pageUrl.pathname.toLowerCase()
    }

    const BASE_CANONICAL = `${storeURL}${getCanonicalPath(canonicalUrl, isSearchPage)}`

    // Sex-Shop/Comestiveis/ADAO-E-EVA?map=c,c,b
    // Brand doesn't appear in the canonical
    // So take the one with the most pathname parts
    if (canonicalUrl.pathname.split('/').length < pageUrl.pathname.split('/').length) {
        canonicalUrl.pathname = pageUrl.pathname
    }

    const ldFiltered = includesProductsInStructuredData ? jsonLD : jsonLD?.breadcrumb

    const page = canonicalUrl.searchParams.get('page')

    // Show ?page=... in the canonical starting on page 2
    if (page === '1') {
        canonicalUrl.searchParams.delete('page')
    }

    let pageTitle = props.title || jsonLD?.seo?.title || query.replace(/\-/g, ' ')

    const unknownPage = jsonLD?.pageInfo.pageTypes?.includes('Unknown')
    const haveTwoOrMoreCategories = categories.length >= 2
    const haveOneOrMoreFilters = otherFilters.length >= 1
    const atLeastTwoCategoriesAndOneFilter = urlMap.length >= 3 && haveTwoOrMoreCategories &&
        haveOneOrMoreFilters

    if (unknownPage) {
        if (atLeastTwoCategoriesAndOneFilter) {
            pageTitle = [...categories, ...otherFilters]
                .reverse()
                .map((i) => capitalize(i.replaceAll('-', ' ').toLowerCase()))
                .join(' - ')
                .split(' ')
                .map((i) => {
                    if (i.length <= 2) return i.toLowerCase()

                    return i
                })
                .join(' ')
        } else {
            pageTitle = URLtoBreadcrumb(url)
                .map(({ name }) => name)
                .reverse()
                .join(' ')
        }
    }

    // Single robots directive combining the CSV noindex/nofollow flags, the
    // empty-page rule (noIndexing) and the search-page noindex — previously
    // spread across two <meta name="robots"> tags (DECO-5444).
    const noindexFinal = noIndexing || props.noindex || isSearchPage
    const nofollowFinal = noIndexing || props.nofollow
    const robots = noindexFinal && nofollowFinal
        ? 'noindex, nofollow'
        : noindexFinal
        ? 'noindex'
        : nofollowFinal
        ? 'nofollow'
        : undefined

    const payload = buildSeoHeadPayload({
        ...props,
        robots,
        title: pageTitle,
        titleTemplate: haveOneOrMoreFiltersAndCategories
            ? props.department.someFilterTitleTemplate
            : isSearchPage
            ? props.search.titleTemplate
            : props.department.titleTemplate,
        description: hasNoFilterOrCategory || hasOnlyCategory
            ? description || props.description || query.replace(/\-/g, ' ')
            : pageTitle,
        descriptionTemplate: isSearchPage ? props.search.descriptionTemplate : '%s',
        canonical: BASE_CANONICAL,
        noIndexing,
        onlyNoIndex: isSearchPage,
        keywords: isDepartmentSearch
            ? 'busca-categoria'
            : isSearchPage
            ? 'busca'
            : isBrandPage
            ? 'marca'
            : 'departamento',
    })

    return {
        ...payload,
        jsonLDs: ldFiltered ? [JSON.stringify(ldFiltered)] : [],
    }
}

// Head tags are emitted once by the route `head()` from the loader output
// (see ~/components/seo/seoHead.ts); the section renders only JSON-LD.
function Section({ jsonLDs, noJsonLD }: Awaited<ReturnType<typeof loader>>) {
    return (
        <>
            {!noJsonLD &&
                jsonLDs?.map((json: string) => (
                    <script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: json }}
                    />
                ))}
        </>
    )
}

export function LoadingFallback() {
    return (
        <div className="h-0 w-0">
        </div>
    )
}

export default Section
